/**
 * What may appear as the first path segment of a clinic's URL, and how a clinic
 * name becomes one.
 *
 * Pure string work with no SQL, DB or React knowledge, because three places have
 * to agree on it exactly:
 *
 *   - the backend, which mints a slug at clinic creation and validates renames
 *   - the SPA bootstrap, which decides whether `/<first-segment>/…` names a
 *     clinic or is one of the app's own global routes
 *   - the `0024` migration, which backfills existing rows (it re-implements
 *     `slugify` in SQL; `clinic-slug.sql-parity.test.ts` pins the two together)
 *
 * A disagreement here is not a cosmetic bug: if the SPA thinks `login` is a
 * clinic slug, the login page becomes unreachable. Hence one definition in a
 * shared package rather than a copy per stack; `@synapse/patient-identifiers` is
 * the precedent.
 *
 * The frontend reaches this through the vite/tsconfig `@synapse/*` aliases, so it
 * must stay dependency-free and runtime-agnostic.
 */

/** Lowercase alphanumeric words joined by single hyphens. No leading, trailing, or doubled hyphens. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Two is the floor because a single character is a typo magnet and reads as a
 * mistake in a URL; forty is the ceiling because the slug prefixes every path in
 * the app and has to stay legible in a browser tab and on a printed QR label.
 */
export const SLUG_MIN_LENGTH = 2;
export const SLUG_MAX_LENGTH = 40;

/**
 * First path segments the app owns, which therefore can never name a clinic.
 *
 * Every entry is a route the SPA or the server serves at the root:
 *   - `api`, `health` — the backend's own surface
 *   - `p` — the global public namespace (the patient portal lives here)
 *   - `login`, `register`, `create-dispensary`, `forgot-password`,
 *     `reset-password` — global auth + onboarding routes
 *   - `admin` — the platform dashboard, which names every clinic rather than one
 *   - `assets`, `static` — build output served by the SPA fallback
 *   - `public`, `www` — not routes today, held back because they are the obvious
 *     names for ones we would add, and a slug is hard to take away once a clinic
 *     has printed it on a QR code. Reserving `admin` ahead of time is exactly why
 *     the platform dashboard could ship without re-homing anybody.
 *
 * Adding a global route means adding it here in the same change, or a clinic
 * that already owns that slug silently loses access to the new page.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  "admin",
  "api",
  "assets",
  "clinics",
  "create-dispensary",
  "forgot-password",
  "health",
  "login",
  "p",
  "public",
  "register",
  "reset-password",
  "static",
  "www",
]);

/**
 * Prefix for the last-resort slug derived from a clinic's id, used when a name
 * yields nothing usable ("清水クリニック", "…", "A"). Shared with the migration's
 * backfill so a hand-built row and a backfilled one look the same.
 */
export const FALLBACK_SLUG_PREFIX = "clinic-";

/**
 * A slug must never be mistakable for a clinic id, because the public routes
 * accept either one in the same position and resolve the id form first. A slug
 * shaped like a UUID would be permanently unreachable.
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Turn a clinic name into a slug candidate.
 *
 * Accents are folded rather than dropped ("Clínica Solé" → "clinica-sole") so a
 * name written properly still produces a slug its staff recognise. Everything
 * outside `[a-z0-9]` becomes a separator, runs collapse, and the result is
 * trimmed to `SLUG_MAX_LENGTH` at a word boundary rather than mid-word.
 *
 * Returns "" when the name has no ASCII-representable characters at all, or
 * yields fewer than `SLUG_MIN_LENGTH` of them. Callers must treat "" as "use the
 * id fallback" — this function deliberately does not invent one, because it has
 * no id to work from.
 */
export function slugify(name: string): string {
  const folded = name
    .normalize("NFD")
    // Strip the combining marks NFD just separated out, leaving the base letter.
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (folded.length <= SLUG_MAX_LENGTH) {
    return folded.length >= SLUG_MIN_LENGTH ? folded : "";
  }

  // Cut at the last word boundary inside the budget so the slug does not end
  // mid-word — but only when that still leaves most of the budget used. A name
  // like "ab-<one very long word>" would otherwise slug to "ab", which says
  // less about the clinic than a hard cut does.
  const clipped = folded.slice(0, SLUG_MAX_LENGTH);
  const lastBoundary = clipped.lastIndexOf("-");
  const trimmed =
    lastBoundary >= Math.floor(SLUG_MAX_LENGTH / 2) ? clipped.slice(0, lastBoundary) : clipped;
  return trimmed.replace(/-+$/g, "");
}

/** Is this a syntactically well-formed slug, ignoring whether it is spoken for? */
export function isWellFormedSlug(value: string): boolean {
  return (
    value.length >= SLUG_MIN_LENGTH &&
    value.length <= SLUG_MAX_LENGTH &&
    SLUG_PATTERN.test(value) &&
    !UUID_PATTERN.test(value)
  );
}

/** Is this segment one the app owns? */
export function isReservedSlug(value: string): boolean {
  return RESERVED_SLUGS.has(value);
}

/**
 * The single question both the API validator and the SPA bootstrap ask: may this
 * string be a clinic's slug at all? Uniqueness is a separate, database-shaped
 * question — see `generateUniqueSlug`.
 */
export function isAssignableSlug(value: string): boolean {
  return isWellFormedSlug(value) && !isReservedSlug(value);
}

/** Last-resort slug for a clinic whose name yields nothing usable. */
export function fallbackSlug(clinicId: string): string {
  return `${FALLBACK_SLUG_PREFIX}${clinicId.replace(/-/g, "").slice(0, 8)}`;
}

/**
 * Nth candidate for a base slug: the base itself, then `base-2`, `base-3`, …
 *
 * The base is shortened when a suffix would push it past `SLUG_MAX_LENGTH`, so
 * candidate 2 of a maximum-length base is still a legal slug rather than a
 * silently-truncated duplicate of candidate 1.
 */
export function suffixedSlug(base: string, n: number): string {
  if (n <= 1) return base;
  const suffix = `-${n}`;
  const room = SLUG_MAX_LENGTH - suffix.length;
  const trunk = base.length <= room ? base : base.slice(0, room).replace(/-+$/g, "");
  return `${trunk}${suffix}`;
}
